import axios from "axios";
import { clearBackendTokenFromSecureStorage } from "@/utils/mobile-secure-token";

const URL = (typeof window === 'undefined' && process.env.INTERNAL_API_URL)
    ? process.env.INTERNAL_API_URL
    : process.env.NEXT_PUBLIC_BACKEND_URL;
axios.defaults.headers.common['Content-Type'] = `application/json`;

export { queryClient } from "@/utils/query-client";

// --- Auth desync guard ---
// Backend 401/403 esetén a kliens oldalon töröljük az auth állapotot, hogy ne maradjon
// "session van, backend nincs" félállapot (mobilon különösen gyakori).
// Kivételek: login/registration jellegű endpointok, ahol a hibát a UI kezeli.
let __authInterceptorInstalled = false;
function ensureAuthInterceptor() {
    if (__authInterceptorInstalled) return;
    __authInterceptorInstalled = true;

    const isAuthExcludedPath = (url = "") => {
        // axios config.url lehet relatív ("/auth/login") vagy abszolút.
        // Egyszerű substring ellenőrzéssel dolgozunk.
        return (
            url.includes('/auth/login') ||
            url.includes('/auth/registration') ||
            url.includes('/auth/activate') ||
            url.includes('/auth/resend') ||
            url.includes('/auth/sendpassword') ||
            url.includes('/auth/setpassword') ||
            url.includes('/auth/setemail') ||
            url.includes('/auth/logout')
        );
    };

    const isUserIdentityEndpoint = (url = "", method = "") => {
        // Csak a konkrét identity check endpoint triggereljen.
        // Frontendben ez: getMyUser() -> GET `${URL}/user`
        // Nem: /user/email, /user/session, stb.
        const m = String(method || '').toLowerCase();
        if (m && m !== 'get') return false;

        const u = String(url || '');
        // egyezés: '/user' vagy '/user?<query>'
        return /\/user(\?|$)/.test(u);
    };

    const forceClientLogout = () => {
        try {
            delete axios.defaults.headers.common['Authorization'];
        } catch (_) {
            // best-effort
        }

        try {
            queryClient.clear();
        } catch (_) {
            // best-effort
        }

        // Mobil secure storage-ban maradt token nélkül a következő app-indításkor
        // restoreAxiosAuthFromSecureStorage() visszaállítaná ugyanezt az érvénytelen tokent.
        clearBackendTokenFromSecureStorage().catch(() => {});

        if (typeof window !== 'undefined') {
            // Ne loopoljon, ha már a login oldalon vagyunk
            if (!window.location.pathname?.startsWith('/bejelentkezes')) {
                window.location.assign('/bejelentkezes');
            }
        }
    };

    const logForcedLogout = ({ status, url, method }) => {
        const payload = {
            status,
            url,
            method,
            time: new Date().toISOString(),
            path: (typeof window !== 'undefined') ? window.location.pathname + window.location.search : 'SSR',
            useragent: (typeof navigator !== 'undefined') ? navigator.userAgent : 'SSR'
        };

        // Mindig logoljuk konzolra (könnyű debug)
        // eslint-disable-next-line no-console
        console.warn('[auth] forced logout triggered (identity endpoint 401/403)', payload);

        // Opcionálisan küldjük a backend bug loggerre is, ha engedélyezve van.
        // Külön axios instance => nem fut rá az itt regisztrált interceptorokra, nem lesz végtelen ciklus.
        if (process.env.NEXT_PUBLIC_DISABLE_ERROR_LOG === "enabled") {
            try {
                const errorAxios = axios.create();
                errorAxios.post(`${URL}/system/bug`, {
                    message: `Forced logout: identity endpoint returned ${status}`,
                    stack: `method: ${method || 'unknown'} | url: ${url || 'unknown'} | path: ${payload.path} | time: ${payload.time}`,
                    url: url || 'unknown',
                    useragent: payload.useragent
                }).catch(() => {});
            } catch (_) {
                // best-effort
            }
        }
    };

    axios.interceptors.response.use(
        (response) => response,
        (error) => {
            const status = error?.response?.status;
            const url = error?.config?.url || '';
            const method = error?.config?.method || '';

            if ((status === 401 || status === 403) && !isAuthExcludedPath(url) && isUserIdentityEndpoint(url, method)) {
                // Fontos: itt nem dobunk tovább semmilyen UI-specifikus hibát, mert a cél
                // az auth állapot egységesítése. A hívó komponens ettől még kaphat rejectet.
                logForcedLogout({ status, url, method });
                forceClientLogout();
            }

            return Promise.reject(error);
        }
    );
}

ensureAuthInterceptor();

const env = process.env.NODE_ENV
if(env == "development"){ 
    if (!globalThis.__axiosDebugRequestInterceptorInstalled) {
        globalThis.__axiosDebugRequestInterceptorInstalled = true;
        axios.interceptors.request.use(config => {
            const url = axios.getUri(config);
            console.debug("Axios GET URL:", url);
            return config;
        });
    }
}

// Response interceptor backend hibák automatikus logolásához
if(process.env.NEXT_PUBLIC_DISABLE_ERROR_LOG === "enabled") {

    if (globalThis.__axiosBackendErrorLogInterceptorInstalled) {
        // már telepítve (HMR / többszöri import ellen)
    } else {
        globalThis.__axiosBackendErrorLogInterceptorInstalled = true;
    
    const shouldSkipAutoBackendLog = (errorData = {}) => {
        const url = String(errorData.url || '');
        const errcode = Number(errorData.errcode || 0);
        const errfield = String(errorData.errfield || '');

        // Ne logoljuk újra magát a log endpointot, különben zajos/önhivatkozó lesz.
        if (url.includes('/system/bug')) return true;

        // Publikus oldalon token hiánynál várható 401 jöhet mellékhívásból.
        if (errcode === 401 && errfield === 'token') return true;

        // Publikus wedding website endpointokon a 4xx user hibák várhatóak.
        const isPublicWebsiteEndpoint = /\/wedding\/\d+\/website\/(guest_check|contact|rsvp)(\?|$)/.test(url);
        if (isPublicWebsiteEndpoint && [400, 404, 409].includes(errcode)) return true;

        return false;
    };

    const logBackendError = (errorData) => {
        if (shouldSkipAutoBackendLog(errorData)) return;

        // Külön axios instance a hibaküléshez, hogy elkerüljük a végtelen ciklust
        const errorAxios = axios.create();
        
        // A backend /system/bug endpoint csak: message, stack, url, useragent mezőket fogad el
        const bugReportData = {
            message: `Backend API Error [${errorData.errcode}]: ${errorData.message}`,
            stack: `errtype: ${errorData.errtype} | errfield: ${errorData.errfield} | http_status: ${errorData.http_status} | source: ${errorData.source} | time: ${errorData.time}`,
            url: errorData.url,
            useragent: typeof navigator !== 'undefined' ? navigator.userAgent : 'SSR'
        };
        
        errorAxios.post(`${URL}/system/bug`, bugReportData)
            .catch(logError => {
                // Ha a backend nem elérhető, helyi tárolás
                console.error("Error log küldés sikertelen:", logError.message);
                
                // localStorage queue későbbi küldéshez (csak client-side)
                if (typeof window !== 'undefined') {
                    try {
                        const errorQueue = JSON.parse(localStorage.getItem('errorQueue') || '[]');
                        errorQueue.push({
                            ...errorData,
                            failed_send: true,
                            retry_count: 0
                        });
                        // Max 50 hibát tárolunk helyben
                        if (errorQueue.length > 50) {
                            errorQueue.shift(); // Legrégebbi törlése
                        }
                        localStorage.setItem('errorQueue', JSON.stringify(errorQueue));
                        console.warn("Hiba mentve helyben későbbi küldéshez:", errorData.errcode);
                    } catch (storageError) {
                        console.error("localStorage mentés sikertelen:", storageError);
                    }
                }
            });
    };

    axios.interceptors.response.use(
        response => {
            // HTTP 200, de success: false és van errcode
            if (response.data?.success === false && response.data?.errcode) {
                const errorData = {
                    message: response.data.message || 'Ismeretlen hiba',
                    errcode: response.data.errcode,
                    errtype: response.data.errtype || 'unknown',
                    errfield: response.data.errfield || '',
                    url: response.config?.url || 'unknown',
                    method: response.config?.method || 'unknown',
                    http_status: response.status,
                    time: new Date().toISOString(),
                    source: "axios-interceptor-success"
                };
                
                logBackendError(errorData);
            }
            return response;
        },
        error => {
            // HTTP 4xx/5xx hibák és van errcode
            if (error.response?.data?.errcode) {
                const errorData = {
                    message: error.response.data.message || 'Ismeretlen hiba',
                    errcode: error.response.data.errcode,
                    errtype: error.response.data.errtype || 'unknown',
                    errfield: error.response.data.errfield || '',
                    url: error.config?.url || 'unknown',
                    method: error.config?.method || 'unknown',
                    http_status: error.response.status,
                    time: new Date().toISOString(),
                    source: "axios-interceptor-error"
                };

                logBackendError(errorData);
            }
            
            // Eredeti hibát továbbítjuk
            return Promise.reject(error);
        }
    );

    }
}
    

/**
 * @param {string} path
 */
export function buildSafeUrl(path) {
    let base = URL;
    if (!base.endsWith('/')) {
        base += '/';
    }
    if (path.startsWith('/')) {
        path = path.substring(1);
    }
    return base + path;
}

export async function manualAuth({ username, password, captcha_response, remember, recaptcha_v2_response, ...rest }) {
    const response = await axios.post(`${URL}/auth/login`, {
        username: username,
        password: password,
        captcha_response: captcha_response,
        remember: remember,
        ...(recaptcha_v2_response ? { recaptcha_v2_response } : {}),
        ...rest,
    });

    return response.data;
}

export async function externalAuth({ params }) {
    const response = await axios.post(`${URL}/auth/login`, params);

    return response.data;
}

export async function tempAdminLogin({ magic_token }) {
    const response = await axios.get(`${URL}/auth/tempAdminLogin`, { params: { token: magic_token } });
    return response.data;
}

export async function getLogout() {
    const response = await axios.get(`${URL}/auth/logout`,);

    try { delete axios.defaults.headers.common['Authorization']; } catch (_) {}
    try { queryClient.clear(); } catch (_) {}
    try { await clearBackendTokenFromSecureStorage(); } catch (_) {}

    return response.data;
}

export async function getMyUser() {
    const response = await axios.get(`${URL}/user`);

    return response.data;
}

export async function getMyWeddings() {
    const response = await axios.get(`${URL}/wedding`);

    return response.data;
}

export async function getGuestListColumns({ wedding_id, setter }) {
    const response = await axios.get(`${URL}/wedding/${wedding_id}/guest/column`, { params: { page: -1 } });

    return response.data.response.column;
}

export async function getGuestList({ wedding_id }) {
    const response = await axios.get(`${URL}/wedding/${wedding_id}/guest`, { params: { page: -1 } });

    return response.data.response.guests;
}

export async function sendGuest({ params, wedding_id }) {
    const response = await axios.post(`${URL}/wedding/${wedding_id}/guest`, { ...params });

    return response.data;
}

export async function patchGuest({ params, wedding_id, id }) {
    const response = await axios.patch(`${URL}/wedding/${wedding_id}/guest/${id}`, { ...params });

    return response.data;
}

export async function explodeGuest({ wedding_id, id }) {
    const response = await axios.delete(`${URL}/wedding/${wedding_id}/guest/connect/${id}`);

    return response.data;
}

export async function saveColumn({ wedding_id, params }) {
    const response = await axios.post(`${URL}/wedding/${wedding_id}/guest/column`, { ...params });

    return response.data;
}

export async function patchColumn({ wedding_id, id, params }) {
    const response = await axios.patch(`${URL}/wedding/${wedding_id}/guest/column/${id}`, { ...params });

    return response.data;
}

export async function colVisibilityAndOrder({ columns, wedding_id }) {
    const response = await axios.patch(`${URL}/wedding/${wedding_id}/guest/column/position`, { column: columns });

    return response.data;
}

export async function deleteColumn({ wedding_id, id }) {
    const response = await axios.delete(`${URL}/wedding/${wedding_id}/guest/column/${id}`);

    return response.data;
}

export async function deleteGuests({ wedding_id, ids }) {
    const response = await axios.delete(`${URL}/wedding/${wedding_id}/guest`, { data: { id: ids } });
    return response.data;
}

export async function userPositions({ wedding_id, positions }) {
    const response = await axios.patch(`${URL}/wedding/${wedding_id}/guest/position`, { position: positions });

    return response.data;
}

export async function linkGuests({ wedding_id, params }) {
    const response = await axios.post(`${URL}/wedding/${wedding_id}/guest/connect`, { ...params });

    return response.data;
}

export async function InlineGuestChange({ params, wedding_id, id }) {
    const response = await axios.put(`${URL}/wedding/${wedding_id}/guest/${id}`, { ...params });

    return response.data;
}

export async function getEventList({ wedding_id }) {
    const response = await axios.get(`${URL}/wedding/${wedding_id}/event`, { params: { page: -1 } });

    return response.data.response.events;
}

export async function getDataForEventCreate({ wedding_id }) {
    const response = await axios.get(`${URL}/wedding/${wedding_id}/event/beforeEdit`);

    return response.data.response;
}

export async function searchProviders({ wedding_id, params }) {
    const response = await axios.post(`${URL}/wedding/${wedding_id}/event/searchProvider`, { ...params });

    return response.data;
}

export async function searchAllProviders({ wedding_id, params }) {
    const response = await axios.post(`${URL}/wedding/${wedding_id}/searchProvider`, { ...params });

    return response.data;
}

export async function createEvent({ wedding_id, params }) {
    const response = await axios.post(`${URL}/wedding/${wedding_id}/event`, { ...params });

    return response.data;
}

export async function editEvent({ wedding_id, params, id }) {
    const response = await axios.patch(`${URL}/wedding/${wedding_id}/event/${id}`, { ...params });

    return response.data;
}

export async function deleteEvent({ wedding_id, id }) {
    const response = await axios.delete(`${URL}/wedding/${wedding_id}/event/${id}`);

    return response.data;
}

export async function editMyUser({ params }) {
    const response = await axios.patch(`${URL}/user`, { ...params });

    return response.data;
}

export async function signUp({ params }) {
    const response = await axios.post(`${URL}/auth/registration`, { ...params });

    return response.data;
}

export async function getTasks({ wedding_id }) {
    const response = await axios.get(`${URL}/wedding/${wedding_id}/task`, { params: { page: -1 } });

    return response.data.response.tasks;
}

export async function getTaskCategories({ wedding_id }) {
    const response = await axios.get(`${URL}/wedding/${wedding_id}/task/category`, { params: { page: -1 } });

    return response.data.response.categories;
}

export async function createTask({ wedding_id, params }) {
    const response = await axios.post(`${URL}/wedding/${wedding_id}/task`, { ...params });

    return response.data;
}

export async function editTask({ wedding_id, params, id }) {
    const response = await axios.patch(`${URL}/wedding/${wedding_id}/task/${id}`, { ...params });

    return response.data;
}

export async function createTaskCategory({ wedding_id, params }) {
    const response = await axios.post(`${URL}/wedding/${wedding_id}/task/category`, { ...params });

    return response.data;
}

export async function editTaskCategory({ wedding_id, params, id }) {
    const response = await axios.patch(`${URL}/wedding/${wedding_id}/task/category/${id}`, { ...params });

    return response.data;
}

export async function setTaskStatus({ wedding_id, params, id }) {
    const response = await axios.put(`${URL}/wedding/${wedding_id}/task/${id}`, { ...params });

    return response.data;
}

export async function deleteTaskCategory({ wedding_id, id }) {
    const response = await axios.delete(`${URL}/wedding/${wedding_id}/task/category/${id}`);

    return response.data;
}

export async function deleteTasks({ wedding_id, ids }) {
    const response = await axios.delete(`${URL}/wedding/${wedding_id}/task`, { data: { id: ids } });

    return response.data;
}

export async function getTaskSamples({ wedding_id }) {
    const response = await axios.get(`${URL}/wedding/${wedding_id}/task/sample`, {
        params: {
            page: -1
        }
    });

    return response.data.response.samples;
}

export async function getTaskProgress({ wedding_id }) {
    const response = await axios.get(`${URL}/wedding/${wedding_id}/task/progress`);

    return response.data.response;
}

export async function createWedding({ params }) {
    const response = await axios.post(`${URL}/wedding`, { ...params });

    return response.data;
}

export async function getCities({ params }) {
    let params_in_string = "";
    if (Object.keys(params.length > 0)) {
        for (let p in params) {
            if (params_in_string !== "") params_in_string += "&";
            else params_in_string += "?";

            params_in_string += `${p}=${params[p]}`
        }
    }

    const response = await axios.get(`${URL}/region/cities${params_in_string}`)

    return response.data.response.cities;
}

export async function getCitiesWithRegions({ params }) {
    let params_in_string = "";
    if (Object.keys(params.length > 0)) {
        for (let p in params) {
            if (params_in_string !== "") params_in_string += "&";
            else params_in_string += "?";

            params_in_string += `${p}=${params[p]}`
        }
    }

    const response = await axios.get(`${URL}/region/all${params_in_string}`)

    let to_return = [];
    if (response.data.response.cities) to_return = [...to_return, ...response.data.response.cities.map(c => ({ city: c, type: "city" }))];
    if (response.data.response.districts) to_return = [...to_return, ...response.data.response.districts.map(c => ({ city: `Budapest ${c}. kerület`, type: "district" }))];
    if (response.data.response.counties) to_return = [...to_return, ...response.data.response.counties.map(c => ({ city: `${c} megye`, type: "county" }))];
    if (response.data.response.regions) to_return = [...to_return, ...response.data.response.regions.map(c => ({ city: `${c} régió`, type: "region" }))];

    return to_return;
}

export async function resendVerifyEmail({ params }) {
    const response = await axios.post(`${URL}/auth/resend`, { ...params });

    return response.data;
}

export async function verifyEmail({ params }) {
    const response = await axios.post(`${URL}/auth/activate`, { ...params });

    return response.data;
}

export async function verifyEmailChange({ params }) {
    const response = await axios.post(`${URL}/auth/setemail`, { ...params });

    return response.data;
}

export async function sendPassword({ params }) {
    const response = await axios.post(`${URL}/auth/sendpassword`, { ...params });

    return response.data;
}

export async function setPassword({ params }) {
    const response = await axios.post(`${URL}/auth/setpassword`, { ...params });

    return response.data;
}

export async function getSeatings({ wedding_id }) {
    const response = await axios.get(`${URL}/wedding/${wedding_id}/seat`, {
        params: {
            page: -1
        }
    });

    return response.data.response.rows;
}

export async function createSeating({ wedding_id, params }) {
    const response = await axios.post(`${URL}/wedding/${wedding_id}/seat`, { ...params });

    return response.data;
}

export async function editSeating({ wedding_id, params, id }) {
    const response = await axios.patch(`${URL}/wedding/${wedding_id}/seat/${id}`, { ...params });

    return response.data;
}

export async function deleteSeating({ wedding_id, id }) {
    const response = await axios.delete(`${URL}/wedding/${wedding_id}/seat/${id}`);

    return response.data;
}

export async function getTables({ wedding_id, seating_id }) {
    const response = await axios.get(`${URL}/wedding/${wedding_id}/seat/${seating_id}/table`, {
        params: {
            page: -1
        }
    });

    return response.data.response.tables;
}

export async function createTable({ wedding_id, seating_id, params }) {
    const response = await axios.post(`${URL}/wedding/${wedding_id}/seat/${seating_id}/table`, { ...params });

    return response.data;
}

export async function editTable({ wedding_id, params, seating_id, id }) {
    const response = await axios.patch(`${URL}/wedding/${wedding_id}/seat/${seating_id}/table/${id}`, { ...params });

    return response.data;
}

export async function deleteTable({ wedding_id, seating_id, id }) {
    const response = await axios.delete(`${URL}/wedding/${wedding_id}/seat/${seating_id}/table/${id}`);

    return response.data;
}

export async function setChair({ wedding_id, params, seating_id }) {
    const response = await axios.patch(`${URL}/wedding/${wedding_id}/seat/${seating_id}/guest`, { ...params });

    return response.data;
}

export async function emailChange({ params }) {
    const response = await axios.patch(`${URL}/user/email`, { ...params });

    return response.data;
}

export async function logoutSessions({ ids }) {
    const response = await axios.delete(`${URL}/user/session`, { data: { id: ids } });

    return response.data;
}

export async function getBudgetList({ wedding_id }) {
    const response = await axios.get(`${URL}/wedding/${wedding_id}/budget`, { params: { page: -1 } });

    return response.data.response;
}

export async function getBudgetSampleList({ wedding_id, category }) {
    const response = await axios.get(`${URL}/wedding/${wedding_id}/budget/sample/${category}`, {
        params: {
            page: -1
        }
    });

    return response.data.response;
}

export async function createBudgetCategory({ wedding_id, params }) {
    const response = await axios.post(`${URL}/wedding/${wedding_id}/budget/category`, { ...params });

    return response.data;
}

export async function patchBudgetCategory({ wedding_id, id, params }) {
    const response = await axios.patch(`${URL}/wedding/${wedding_id}/budget/category/${id}`, { ...params });

    return response.data;
}

export async function deleteBudgetCategory({ wedding_id, id }) {
    const response = await axios.delete(`${URL}/wedding/${wedding_id}/budget/category/${id}`);

    return response.data;
}

export async function sendBudget({ params, wedding_id }) {
    const response = await axios.post(`${URL}/wedding/${wedding_id}/budget`, { ...params });

    return response.data;
}

export async function patchBudget({ params, wedding_id, id }) {
    const response = await axios.patch(`${URL}/wedding/${wedding_id}/budget/${id}`, { ...params });

    return response.data;
}

export async function deleteBudgets({ wedding_id, ids }) {
    const response = await axios.delete(`${URL}/wedding/${wedding_id}/budget`, { data: { id: ids } });

    return response.data;
}

export async function budgetPositions({ wedding_id, positions }) {
    const response = await axios.patch(`${URL}/wedding/${wedding_id}/budget/position`, { position: positions });

    return response.data;
}

export async function changeBudget({ wedding_id, val }) {
    const response = await axios.put(`${URL}/wedding/${wedding_id}`, { field: "budget", value: val });

    return response.data;
}

export async function changeWeddingDate({ wedding_id, val }) {
    const response = await axios.put(`${URL}/wedding/${wedding_id}`, { field: "date", value: val });

    return response.data;
}

export async function getEnvConfig() {
    const response = await axios.get(`${URL}/system/envConfig`);

    return response.data;
}

export async function sendErrorLog({ params }) {
    const response = await axios.post(`${URL}/system/bug`, { ...params });

    return response.data;
}

export async function sendHelpMessage({ params }) {
    const response = await axios.post(`${URL}/system/help`, { ...params });

    return response.data;
}

export async function getProviders({ wedding_id }) {
    const response = await axios.get(`${URL}/wedding/${wedding_id}/provider`, { params: { page: -1 } });

    return response.data.response.providers;
}

export async function getOneProvider({ wedding_id, provider_id }) {
    const response = await axios.get(`${URL}/wedding/${wedding_id}/provider/${provider_id}`);

    return response.data.response.provider;
}

export async function getProviderTypes() {
    const response = await axios.get(`${URL}/provider/type`, { params: { page: -1 } });

    return response.data.response.categories;
}

export async function getProviderTypesByCity({ filter_param, city_value }) {
    const params = { page: -1, [`filter[${filter_param}]`]: city_value };
    const response = await axios.get(`${URL}/provider_type/cat`, { params });
    return response.data.response.categories;
}

export async function getProviderTypesWedding() {
    const response = await axios.get(`${URL}/provider_type/wedding`);

    return response.data.response.categories;
}

export async function sendNewCustomProvider({ params, wedding_id }) {
    const response = await axios.post(`${URL}/wedding/${wedding_id}/provider`, { ...params });

    return response.data;
}

export async function changeInlineProvider({ provider_id, wedding_id, field, val }) {
    const response = await axios.put(`${URL}/wedding/${wedding_id}/provider/${provider_id}`, { field: field, value: val });

    return response.data;
}

export async function patchCustomProvider({ params, wedding_id, id }) {
    const response = await axios.patch(`${URL}/wedding/${wedding_id}/provider/${id}`, { ...params });

    return response.data;
}

export async function deleteProvider({ wedding_id, id }) {
    const response = await axios.delete(`${URL}/wedding/${wedding_id}/provider/${id}`);

    return response.data;
}

export async function getAllowedProviderFileTypes({ provider_id }) {
    const response = await axios.get(`${URL}/provider/${provider_id}/attachment_mime`, { params: { page: -1 } });

    return response.data.response;
}

export async function getAllowedFileTypes({ wedding_id }) {
    const response = await axios.get(`${URL}/wedding/${wedding_id}/provider/message_attachment_mime`, { params: { page: -1 } });

    return response.data.response;
}

export async function deleteMessageItem({ wedding_id, message_id }) {
    const response = await axios.delete(`${URL}/wedding/${wedding_id}/provider/message/${message_id}`);

    return response.data.response;
}

export async function getPreviousOffer({ wedding_id, provider_id }) {
    const response = await axios.get(`${URL}/wedding/${wedding_id}/provider/${provider_id}/beforeOffer`);

    return response.data.response;
}

export async function sendNewOffer({ params, wedding_id, provider_id }) {
    const response = await axios.post(`${URL}/wedding/${wedding_id}/provider/${provider_id}/message`, { ...params });

    return response.data;
}


export async function uploadAttachment({ params, wedding_id, provider_id, setProgress, cancelToken }) {
    let config = {};
    if (setProgress) config = {
        onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setProgress(percentCompleted);
        },
    };
    if (cancelToken) config = {
        ...config,
        cancelToken: cancelToken.token
    }

    const response = await axios.post(`${URL}/wedding/${wedding_id}/provider/${provider_id}/message/attachment`, { ...params }, config);

    return response.data;
}

export async function sendProviderRead({ wedding_id, provider_id }) {
    const response = await axios.post(`${URL}/wedding/${wedding_id}/provider/${provider_id}/message/read`);

    return response.data;
}

export async function getWebsiteSectionList({ wedding_id }) {
    const response = await axios.get(`${URL}/wedding/${wedding_id}/website/section`);

    return response.data.response;
}

export async function getSectionTypeList({ wedding_id }) {
    const response = await axios.get(`${URL}/wedding/${wedding_id}/website/section_type`);

    return response.data.response.types;
}

export async function sendWebsiteSectionPositions({ wedding_id, ids }) {
    const response = await axios.patch(`${URL}/wedding/${wedding_id}/website/section/position`, { position: ids });

    return response.data;
}

export async function changeWebsitePublicState({ wedding_id, val }) {
    const response = await axios.patch(`${URL}/wedding/${wedding_id}/website/public`, { is_public: val });

    return response.data;
}

export async function sendWebsiteNewSection({ wedding_id, type, event_id, title, description }) {
    const payload = { type, ...(title !== undefined ? {title} : {}), ...(description !== undefined ? {description} : {}) };
    if (event_id) payload.event_id = event_id;
    
    const response = await axios.post(`${URL}/wedding/${wedding_id}/website/section`, payload);

    return response.data;
}

export async function deleteSection({ wedding_id, id }) {
    const response = await axios.delete(`${URL}/wedding/${wedding_id}/website/section/${id}`);

    return response.data;
}

export async function patchWebsiteSection({ params, wedding_id, section_id }) {
    const response = await axios.patch(`${URL}/wedding/${wedding_id}/website/section/${section_id}`, { ...params });

    return response.data;
}

export async function resetEventImageToProvider({ wedding_id, section_id }) {
    const response = await axios.delete(`${URL}/wedding/${wedding_id}/website/section/${section_id}/reset_image`);

    return response.data;
}

export async function getAvailableEvents({ wedding_id }) {
    const response = await axios.get(`${URL}/wedding/${wedding_id}/website/available_events`);

    return response.data;
}

export async function getWebsiteSectionListByUrl({ share_url }) {
    const response = await axios.get(`${URL}/wedding_website/public/${share_url}`);

    return response.data;
}

export async function sendWebsiteShareEmail({ wedding_id, params }) {
    const response = await axios.post(`${URL}/wedding/${wedding_id}/website/share_email`, { ...params });

    return response.data;
}

export async function getEmailTemplates({ wedding_id, type = 'wedding_messages_template' }) {
    // SQL-based dynamic filtering - backend filters by show_in field
    const params = type ? { type } : {};
    const response = await axios.get(`${URL}/wedding/${wedding_id}/website/email_templates`, { params });
    return response.data.response?.templates || response.data.templates || [];
}

export async function sendWebsiteContact({ wedding_id, params }) {
    const response = await axios.post(`${URL}/wedding/${wedding_id}/website/contact`, { ...params });

    return response.data;
}

export async function searchWebsiteUser({ wedding_id, params }) {
    const response = await axios.post(`${URL}/wedding/${wedding_id}/website/guest_check`, { ...params });

    return response.data;
}

export async function sendWebsiteRSVPForm({ wedding_id, params }) {
    const response = await axios.post(`${URL}/wedding/${wedding_id}/website/rsvp`, { ...params });

    return response.data;
}

export async function syncWebsiteRSVP({ wedding_id, message_id }) {
    const url = message_id 
        ? `${URL}/wedding/${wedding_id}/website/rsvp/sync/${message_id}`
        : `${URL}/wedding/${wedding_id}/website/rsvp/sync`;
    const response = await axios.post(url);

    return response.data;
}

export async function getNotifications() {
    const response = await axios.get(`${URL}/notification`, { params: { page: -1 } });

    return response.data.response.notifications;
}

export async function setNotificationsStatus({ id }) {
    const response = await axios.patch(`${URL}/notification/read/${id}`);

    return response.data;
}

export async function sendGuestRead({ wedding_id, guest_id }) {
    const response = await axios.post(`${URL}/wedding/${wedding_id}/guest/${guest_id}/message/read`);

    return response.data;
}

export async function getWedding({ wedding_id }) {
    const response = await axios.get(`${URL}/wedding/${wedding_id}`);

    return response.data?.response?.wedding;
}

export async function getOnlyWeddingCategory({ }) {
    const response = await axios.get(`${URL}/provider_type/wedding`);

    return response.data.response.categories;
}

export async function getProviderMessages({ wedding_id }) {
    const response = await axios.get(`${URL}/wedding/${wedding_id}/provider/messages`, { params: { page: -1 } });

    return response.data.response.messages;
}

export async function getGuestMessages({ wedding_id }) {
    const response = await axios.get(`${URL}/wedding/${wedding_id}/guest/messages`, { params: { page: -1 } });

    return response.data.response.messages;
}

export async function editWedding({ wedding_id, params }) {
    const response = await axios.patch(`${URL}/wedding/${wedding_id}`, { ...params });

    return response.data;
}

export async function getProvidersByFilter({ params, pager = false }) {
    const response = await axios.get(`${URL}/provider`, { params: { ...params } });

    if (pager) return response.data;
    return response.data.response;
}

export async function getHomepageMosaic() {
    const response = await axios.get(`${URL}/provider/homepage_mosaic`);

    return response.data.response;
}

export async function getProviderById({ id, params }) {
    const response = await axios.get(`${URL}/provider${id === 0 ? "" : `/${id}`}`, { params: { ...params } });
    return response.data;
}

export async function getNewsCategory({ id, params }) {
    const response = await axios.get(`${URL}/news/category${id === 0 ? "" : `/${id}`}`, { params: { ...params } });

    return response.data;
}

export async function getNewsMostread({ params }) {
    const response = await axios.get(`${URL}/news/mostread`, { params: { ...params } });

    return response.data;
}

export async function getNewsLatest({ params }) {
    const response = await axios.get(`${URL}/news/latest`, { params: { ...params } });

    return response.data;
}

export async function getNewsById({ id, params }) {
    const response = await axios.get(`${URL}/news${id === 0 ? "" : `/${id}`}`, { params: { ...params } });

    return response.data;
}

export async function addReadToNews({ id }) {
    const response = await axios.patch(`${URL}/news/read/${id}`);

    return response.data;
}

export async function getMeta({ type, params }) {
    const response = await axios.post(`${URL}/system/meta${type ? `/${type}` : ""}`, { ...params });

    return response.data.response;
}

export async function checkEmail({ params }) {
    const response = await axios.post(`${URL}/system/emailcheck`, { ...params });

    return response.data.response;
}

export async function sendNewOfferPublic({ params, provider_id }) {
    const response = await axios.post(`${URL}/provider/message/${provider_id}`, { ...params });

    return response.data;
}

export async function getBanners({ category }) {
    const response = await axios.get(`${URL}/banner/${category}`);

    return response.data;
}

export async function getProviderQuestions({ category }) {
    const response = await axios.get(`${URL}/provider_create/questions/${category}`);

    return response.data.response;
}

export async function getProviderContracts({ category, questions, params }) {
    const response = await axios.get(`${URL}/provider_create/packages/${category}/${questions}`, { params: { ...params } });

    return response.data;
}

export async function getProviderContractsRenew({ provider_id, params }) {
    const response = await axios.get(`${URL}/provider_create/renew_packages/${provider_id}`, { params: { ...params } });

    return response.data;
}

export async function advertiserRegistration({ params }) {
    const response = await axios.post(`${URL}/provider_create/order`, { ...params });

    return response.data;
}

export async function getMyAds({ }) {
    const response = await axios.get(`${URL}/provider/manage`, { params: { page: -1 } });

    return response.data.response;
}

export async function getAdShare({ id }) {
    const response = await axios.get(`${URL}/provider/${id}/share`, { params: { page: -1 } });

    return response.data.response;
}

export async function sendAdShare({ id, params }) {
    const response = await axios.post(`${URL}/provider/${id}/share`, { ...params });

    return response.data;
}

export async function sendAdShareInline({ id, params }) {
    const response = await axios.put(`${URL}/provider/${id}/share`, { ...params });

    return response.data;
}

export async function deleteAdShare({ id, provider_id, is_pending }) {
    const response = await axios.delete(`${URL}/provider/${provider_id}/share/${id}${is_pending ? "/pending" : ""}`);

    return response.data;
}

export async function getConsentRequest({ token }) {
    const response = await axios.get(`${URL}/consent/request/${token}`);

    return response.data;
}

export async function sendConsentAnswer({ token, answer }) {
    const response = await axios.post(`${URL}/consent/answer/${token}`, { answer });

    return response.data;
}

export async function verifyProviderInvitation({ hash }) {
    const response = await axios.post(`${URL}/provider_share/accept_request/${hash}`);

    return response.data;
}

export async function getAllowedFileTypesProvider({ provider_id }) {
    const response = await axios.get(`${URL}/provider/${provider_id}/manage/attachment_mime`);

    return response.data.response;
}

export async function getProviderAcceptRequest({ provider_id }) {
    const response = await axios.get(`${URL}/provider/${provider_id}/manage/accept_request`);

    return response.data.response;
}

export async function editService({ provider_id, params }) {
    const response = await axios.patch(`${URL}/provider/${provider_id}/manage`, { ...params });

    return response.data;
}

export async function getSitemap({ type }) {
    const response = await axios.get(`${URL}/sitemap/${type}?page=-1`);

    return response.data;
}

export async function uploadAttachmentProvider({ params, provider_id/*, setProgress, cancelToken*/ }) {
    /*let config = {};
    if(setProgress) config = {
        onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setProgress(percentCompleted);
        },
    };
    if(cancelToken) config = {
        ...config,
        cancelToken: cancelToken.token
    }*/

    const response = await axios.post(`${URL}/provider/${provider_id}/manage/attachment`, { ...params }/*, config*/);

    return response.data;
}

export async function uploadAttachmentProviderStorage({ params, provider_id/*, setProgress, cancelToken*/ }) {
    /*let config = {};
    if(setProgress) config = {
        onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setProgress(percentCompleted);
        },
    };
    if(cancelToken) config = {
        ...config,
        cancelToken: cancelToken.token
    }*/

    const response = await axios.post(`${URL}/provider/${provider_id}/manage/storage`, { ...params }/*, config*/);

    return response.data;
}

export async function uploadAttachmentProviderGallery({ params, provider_id/*, setProgress, cancelToken*/ }) {
    /*let config = {};
    if(setProgress) config = {
        onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setProgress(percentCompleted);
        },
    };
    if(cancelToken) config = {
        ...config,
        cancelToken: cancelToken.token
    }*/

    const response = await axios.post(`${URL}/provider/${provider_id}/manage/gallery`, { ...params }/*, config*/);

    return response.data;
}

export async function renameProviderGallery({ name, provider_id, id }) {
    const response = await axios.patch(`${URL}/provider/${provider_id}/manage/gallery/${id}`, { description: name });

    return response.data;
}

export async function galleryPosition({ provider_id, positions }) {
    const response = await axios.patch(`${URL}/provider/${provider_id}/manage/galleryposition`, { position: positions });

    return response.data;
}

export async function getMyProviderMessages({ provider_id }) {
    const response = await axios.get(`${URL}/provider/${provider_id}/manage/messages`, { params: { page: -1 } });

    return response.data;
}

export async function sendMessagesInnerNote({ provider_id, wedding_id, params }) {
    const response = await axios.post(`${URL}/provider/${provider_id}/manage/message_inner_note/${wedding_id}`, { ...params });

    return response.data;
}

export async function sendMyProviderRead({ wedding_id, email, provider_id }) {
    const response = await axios.post(`${URL}/provider/${provider_id}/manage/message_read/${wedding_id}`, { email: email });

    return response.data;
}

export async function deleteMyProviderMessage({ wedding_id, provider_id }) {
    const response = await axios.delete(`${URL}/provider/${provider_id}/manage/message/${wedding_id}`);

    return response.data;
}

export async function sendNewProviderMessage({ params, wedding_id, provider_id }) {
    const response = await axios.post(`${URL}/provider/${provider_id}/manage/message/${wedding_id}`, { ...params });

    return response.data;
}

export async function getMyProviderDocuments({ provider_id }) {
    const response = await axios.get(`${URL}/provider/${provider_id}/manage/storage`, { params: { page: -1 } });

    return response.data;
}

export async function getMyProviderGallery({ provider_id }) {
    const response = await axios.get(`${URL}/provider/${provider_id}/manage/gallery`, { params: { page: -1 } });

    return response.data;
}

export async function deleteItemFromStorage({ provider_id, id }) {
    const response = await axios.delete(`${URL}/provider/${provider_id}/manage/storage/${id}`);

    return response.data;
}

export async function deleteItemFromGallery({ provider_id, id }) {
    const response = await axios.delete(`${URL}/provider/${provider_id}/manage/gallery/${id}`);

    return response.data;
}

export async function getMyProviderDrafts({ provider_id }) {
    const response = await axios.get(`${URL}/provider/${provider_id}/messagetemplate`, { params: { page: -1 } });

    return response.data;
}

export async function createNewDarft({ provider_id, params }) {
    const response = await axios.post(`${URL}/provider/${provider_id}/messagetemplate`, { ...params });

    return response.data;
}

export async function editDarft({ provider_id, params, id }) {
    const response = await axios.patch(`${URL}/provider/${provider_id}/messagetemplate/${id}`, { ...params });

    return response.data;
}

export async function deleteDarft({ provider_id, id }) {
    const response = await axios.delete(`${URL}/provider/${provider_id}/messagetemplate/${id}`);

    return response.data;
}

export async function resendShareLink({ provider_id, id }) {
    const response = await axios.post(`${URL}/provider/${provider_id}/share/resend`, { id: id });

    return response.data;
}

export async function deleteOneMessage({ wedding_id, message_id }) {
    const response = await axios.delete(`${URL}/wedding/${wedding_id}/provider/message/${message_id}`);

    return response.data;
}

export async function phoneClick({ id }) {
    const response = await axios.post(`${URL}/provider/${id}/click_phone`);

    return response.data;
}

export async function budgetCategoryPositions({ wedding_id, positions }) {
    const response = await axios.patch(`${URL}/wedding/${wedding_id}/budget/category_position`, { position: positions });

    return response.data;
}